import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import './Pages.css';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const reports = [
    { 
      id: 'production-per-product', 
      title: 'Production per Product', 
      type: 'Production', 
      icon: '📊',
      description: 'View production statistics grouped by insurance products'
    },
    { 
      id: 'client-growth', 
      title: 'Client Growth Report', 
      type: 'Clients', 
      icon: '👥',
      description: 'Track new customer acquisition and growth trends'
    },
    {
      id: 'performance',
      title: 'Performance Report',
      type: 'Performance',
      icon: '📈',
      description: 'Overall business performance metrics and revenue analysis'
    }
  ];

  const formatCurrency = (amount) => {
    if (!amount) return 'KES 0.00';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const fetchReport = async (reportId) => {
    setLoading(true);
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/reports/${reportId}`);
      const data = await response.json();

      if (data.success) {
        setReportData(data);
        setSelectedReport(reports.find(r => r.id === reportId));
        setShowModal(true);
      } else {
        alert('Failed to load report');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      alert('Unable to load report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReportPDF = () => {
    if (!reportData || !selectedReport) return;

    const doc = new jsPDF();
    const timestamp = new Date().toLocaleString();
    
    // Header
    doc.setFillColor(102, 126, 234);
    doc.rect(0, 0, 210, 30, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('ZAKIM INSURANCE AGENCY', 105, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.text(selectedReport.title, 105, 23, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text(`Generated: ${timestamp}`, 20, 40);

    let y = 50;

    // Report-specific content
    if (selectedReport.id === 'production-per-product') {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Production by Product', 20, y);
      y += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      reportData.data.forEach((item, index) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${index + 1}. ${item.ProductName} (${item.ProductCode})`, 20, y);
        y += 7;
        doc.text(`   Total Policies: ${item.TotalPolicies}`, 25, y);
        y += 6;
        doc.text(`   Total Premium: ${formatCurrency(item.TotalPremium)}`, 25, y);
        y += 6;
        doc.text(`   Avg Premium: ${formatCurrency(item.AveragePremium)}`, 25, y);
        y += 10;
      });
    } else if (selectedReport.id === 'client-growth') {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Client Growth Summary', 20, y);
      y += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const summary = reportData.summary;
      doc.text(`Total Clients: ${summary.TotalClients}`, 20, y);
      y += 7;
      doc.text(`New Clients (Last 30 Days): ${summary.Last30Days}`, 20, y);
      y += 7;
      doc.text(`New Clients (Last 90 Days): ${summary.Last90Days}`, 20, y);
      y += 15;

      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Growth', 20, y);
      y += 10;

      doc.setFont('helvetica', 'normal');
      reportData.monthly.forEach((item, index) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${item.Month}: ${item.NewClients} new clients`, 20, y);
        y += 7;
      });
    } else if (selectedReport.id === 'performance') {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Performance Overview', 20, y);
      y += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const policies = reportData.policies;
      doc.text(`Total Policies: ${policies.TotalPolicies}`, 20, y);
      y += 7;
      doc.text(`Active Policies: ${policies.ActivePolicies}`, 20, y);
      y += 7;
      doc.text(`Total Premium: ${formatCurrency(policies.TotalPremium)}`, 20, y);
      y += 7;
      doc.text(`Average Premium: ${formatCurrency(policies.AveragePremium)}`, 20, y);
      y += 15;

      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Revenue', 20, y);
      y += 10;

      doc.setFont('helvetica', 'normal');
      reportData.revenue.forEach((item) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${item.Month}: ${formatCurrency(item.MonthlyRevenue)}`, 20, y);
        y += 7;
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
    }

    doc.save(`${selectedReport.title.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
  };

  const downloadReportCSV = () => {
    if (!reportData || !selectedReport) return;

    let csvContent = `${selectedReport.title}\nGenerated: ${new Date().toLocaleString()}\n\n`;

    if (selectedReport.id === 'production-per-product') {
      csvContent += 'Product Name,Product Code,Total Policies,Total Premium,Total Commission,Average Premium\n';
      reportData.data.forEach(item => {
        csvContent += `${item.ProductName},${item.ProductCode},${item.TotalPolicies},${item.TotalPremium},${item.TotalCommission},${item.AveragePremium}\n`;
      });
    } else if (selectedReport.id === 'client-growth') {
      csvContent += 'Month,New Clients,Unique Clients\n';
      reportData.monthly.forEach(item => {
        csvContent += `${item.Month},${item.NewClients},${item.UniqueClients}\n`;
      });
    } else if (selectedReport.id === 'performance') {
      csvContent += 'Month,Revenue,Commission,Transaction Count\n';
      reportData.revenue.forEach(item => {
        csvContent += `${item.Month},${item.MonthlyRevenue},${item.MonthlyCommission},${item.TransactionCount}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedReport.title.replace(/\s+/g, '_')}_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderReportContent = () => {
    if (!reportData || !selectedReport) return null;

    switch (selectedReport.id) {
      case 'production-per-product':
        return (
          <div className="report-content">
            <h3>Production by Product</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Product Code</th>
                    <th>Total Policies</th>
                    <th>Total Premium</th>
                    <th>Avg Premium</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.data.map((item, index) => (
                    <tr key={index}>
                      <td>{item.ProductName}</td>
                      <td>{item.ProductCode}</td>
                      <td>{item.TotalPolicies}</td>
                      <td className="amount">{formatCurrency(item.TotalPremium)}</td>
                      <td className="amount">{formatCurrency(item.AveragePremium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'client-growth':
        return (
          <div className="report-content">
            <h3>Client Growth Summary</h3>
            <div className="info-grid">
              <div className="info-card">
                <h4>Total Clients</h4>
                <p className="stat-number">{reportData.summary.TotalClients}</p>
              </div>
              <div className="info-card">
                <h4>Last 30 Days</h4>
                <p className="stat-number">{reportData.summary.Last30Days}</p>
              </div>
              <div className="info-card">
                <h4>Last 90 Days</h4>
                <p className="stat-number">{reportData.summary.Last90Days}</p>
              </div>
            </div>
            <h3>Monthly Growth</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>New Clients</th>
                    <th>Unique Clients</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.monthly.map((item, index) => (
                    <tr key={index}>
                      <td>{item.Month}</td>
                      <td>{item.NewClients}</td>
                      <td>{item.UniqueClients}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'performance':
        return (
          <div className="report-content">
            <h3>Policy Overview</h3>
            <div className="info-grid">
              <div className="info-card">
                <h4>Total Policies</h4>
                <p className="stat-number">{reportData.policies.TotalPolicies}</p>
              </div>
              <div className="info-card">
                <h4>Active Policies</h4>
                <p className="stat-number">{reportData.policies.ActivePolicies}</p>
              </div>
              <div className="info-card">
                <h4>Total Premium</h4>
                <p className="stat-number">{formatCurrency(reportData.policies.TotalPremium)}</p>
              </div>
            </div>
            <h3>Monthly Revenue</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Revenue</th>
                    <th>Commission</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.revenue.map((item, index) => (
                    <tr key={index}>
                      <td>{item.Month}</td>
                      <td className="amount">{formatCurrency(item.MonthlyRevenue)}</td>
                      <td className="amount">{formatCurrency(item.MonthlyCommission)}</td>
                      <td>{item.TransactionCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      default:
        return <p>No data available</p>;
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>📊 Reports & Analytics</h1>
          <p className="page-subtitle">Generate and download comprehensive business reports</p>
        </div>
      </div>

      <div className="reports-grid">
        {reports.map(report => (
          <div key={report.id} className="report-card">
            <div className="report-icon">{report.icon}</div>
            <h3>{report.title}</h3>
            <p className="report-description">{report.description}</p>
            <div className="report-meta">
              <span className="report-type">{report.type}</span>
            </div>
            <div className="report-actions">
              <button 
                className="report-btn view-btn" 
                onClick={() => fetchReport(report.id)}
                disabled={loading}
              >
                👁️ View
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Report Modal */}
      {showModal && reportData && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content-wide report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedReport.icon} {selectedReport.title}</h2>
                <p className="modal-subtitle">Generated: {new Date(reportData.timestamp).toLocaleString()}</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              {renderReportContent()}
            </div>

            <div className="modal-footer">
              <button className="secondary-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
              <button className="primary-btn" onClick={downloadReportPDF}>
                📄 Download PDF
              </button>
              <button className="primary-btn" onClick={downloadReportCSV}>
                📊 Download CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="modal-overlay">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Generating report...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
